/**
 * Point d'amarrage : porteur allié (phase B) ou baie de la station. Géométrie de collision en boîtes, tunnel
 * traversant (entrée en poupe, sortie en proue), appontage arcade : entrer dans la zone d'approche lentement
 * et aligné → séquence guidée → service (et menus de dock) → catapulte.
 */
import type { DockData, DockId } from '../../data';
import { Quat, Vec3 } from '../core/math';
import type { Ship } from '../entities';

export interface WorldBox {
  id: string;
  center: Vec3;
  rotation: Quat;
  half: Vec3;
}

export interface DockReadiness {
  inZone: boolean;
  speedOk: boolean;
  alignOk: boolean;
}

export class Dock {
  readonly position: Vec3;
  readonly rotation: Quat;

  constructor(readonly id: DockId, readonly data: DockData) {
    this.position = Vec3.from(data.position);
    this.rotation = Quat.from(data.rotation);
  }

  toLocal(p: Vec3): Vec3 {
    return this.rotation.conjugate().rotate(p.sub(this.position));
  }

  toWorld(local: Vec3 | readonly number[]): Vec3 {
    const v = local instanceof Vec3 ? local : Vec3.from(local);
    return this.position.add(this.rotation.rotate(v));
  }

  forward(): Vec3 {
    return this.rotation.forward();
  }

  /** Boîtes de collision en coordonnées monde. */
  boxes(): WorldBox[] {
    return this.data.parts.map((part) => ({
      id: `${this.id}-${part.id}`,
      center: this.toWorld(part.center),
      rotation: this.rotation.clone(),
      half: Vec3.from(part.half),
    }));
  }

  padWorld(): Vec3 {
    return this.toWorld(this.data.pad);
  }

  /** Entrée du tunnel côté poupe, à hauteur du plot. */
  tunnelEntrance(): Vec3 {
    const pad = this.data.pad;
    return this.toWorld([pad[0]!, pad[1]!, this.data.tunnel.halfLength]);
  }

  /** Point de sortie de catapulte, au-delà de la proue. */
  launchExit(): Vec3 {
    const pad = this.data.pad;
    return this.toWorld([pad[0]!, pad[1]!, -this.data.tunnel.halfLength - 40]);
  }

  inTunnel(p: Vec3): boolean {
    const l = this.toLocal(p);
    const t = this.data.tunnel;
    return Math.abs(l.x) < t.halfWidth && Math.abs(l.y) < t.halfHeight && Math.abs(l.z) < t.halfLength;
  }

  inApproach(p: Vec3): boolean {
    const l = this.toLocal(p);
    const a = this.data.approach;
    return Math.abs(l.x - a.center[0]!) < a.half[0]! && Math.abs(l.y - a.center[1]!) < a.half[1]! && Math.abs(l.z - a.center[2]!) < a.half[2]!;
  }

  readiness(ship: Ship): DockReadiness {
    return {
      inZone: this.inApproach(ship.position) || this.inTunnel(ship.position),
      speedOk: ship.velocity.length() <= this.data.dockMaxSpeed,
      alignOk: ship.forward().dot(this.forward()) >= this.data.dockMinAlignment,
    };
  }

  /** Distance à l'enveloppe de protection (négative à l'intérieur), pour l'IA ennemie. */
  keepOutDepth(p: Vec3): number {
    return this.data.keepOutRadius - p.distanceTo(this.position);
  }
}

const smooth = (t: number) => t * t * (3 - 2 * t);

/** Position de la séquence d'appontage à l'instant `t` ∈ [0, 1] : vers l'entrée du tunnel puis vers le plot. */
export function dockingPose(carrier: Dock, from: Vec3, fromRotation: Quat, t: number): { position: Vec3; rotation: Quat } {
  const entrance = carrier.tunnelEntrance();
  const pad = carrier.padWorld();
  const startsInside = carrier.inTunnel(from);
  let position: Vec3;
  if (startsInside) {
    position = from.lerp(pad, smooth(Math.min(1, t)));
  } else if (t < 0.45) {
    position = from.lerp(entrance, smooth(t / 0.45));
  } else {
    position = entrance.lerp(pad, smooth((t - 0.45) / 0.55));
  }
  const rotation = fromRotation.slerp(carrier.rotation, smooth(Math.min(1, t / 0.6))).normalized();
  return { position, rotation };
}

/** Catapulte : accélération du plot jusqu'au-delà de la proue. */
export function launchPose(carrier: Dock, t: number): Vec3 {
  const eased = Math.min(1, t) ** 2;
  return carrier.padWorld().lerp(carrier.launchExit(), eased);
}
